import {
  COVOMO_SET_DISPLAY_PREFERENCES_TYPE,
  parseEmbedDisplaySettings,
  type EmbedDisplaySettingsInput,
} from "@/utils/embedDisplaySettings";
import { setA11yDisplayPreferences } from "@/composables/useA11yDisplayPreferences";
import { schedulePostDisplayPreferencesStateToParent } from "@/composables/embed/postDisplayPreferencesStateToParent";
import type { AppLocale } from "@/composables/appLocale";

type DisplayPreferencesMessage = EmbedDisplaySettingsInput & {
  type?: string;
};

let localeApplier: ((locale: AppLocale) => void) | null = null;

export function registerDisplayPreferencesLocaleApplier(
  fn: (locale: AppLocale) => void,
): void {
  localeApplier = fn;
}

export function unregisterDisplayPreferencesLocaleApplier(): void {
  localeApplier = null;
}

export function applyDisplayPreferencesFromParentPayload(
  raw: EmbedDisplaySettingsInput,
): void {
  const { locale, displayPrefs } = parseEmbedDisplaySettings(raw);

  if (Object.keys(displayPrefs).length > 0) {
    setA11yDisplayPreferences(displayPrefs);
  }
  if (locale && localeApplier) {
    localeApplier(locale);
  }
  schedulePostDisplayPreferencesStateToParent();
}

function isCovomoSetDisplayPreferencesMessage(
  data: unknown,
): data is DisplayPreferencesMessage {
  if (!data || typeof data !== "object") return false;
  const t = (data as DisplayPreferencesMessage).type;
  return t === COVOMO_SET_DISPLAY_PREFERENCES_TYPE;
}

let registeredHandler: ((ev: MessageEvent) => void) | null = null;

export function registerParentDisplayPreferencesPostMessageListener(): void {
  if (typeof window === "undefined") return;
  if (registeredHandler) return;
  registeredHandler = (ev: MessageEvent): void => {
    if (ev.source !== window.parent) return;
    if (!isCovomoSetDisplayPreferencesMessage(ev.data)) return;
    applyDisplayPreferencesFromParentPayload(ev.data);
  };
  window.addEventListener("message", registeredHandler);
}

export function unregisterParentDisplayPreferencesPostMessageListener(): void {
  if (typeof window === "undefined" || !registeredHandler) return;
  window.removeEventListener("message", registeredHandler);
  registeredHandler = null;
}
